import SectionVisible from 'appContainer/SectionVisible';
import CheckSubmitImage from 'assett/icons/check_eligibility_image.svg';
import { resetDateOfService } from 'feature/form/formSlice';
import { resetMember } from 'feature/members/memberSlice';
import { resetMembers } from 'feature/members/membersSlice';
import useDevice from 'hooks/DesktopDevice.hook';
import { useCheckEligibility } from 'hooks/useCarrierCustomization';
import { FC, Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import styled from 'styled-components';
import {
	BoxOverflow,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectItemText,
	Typography,
} from 'styles';
import Box from 'styles/box/Box';
import CardMedia from 'styles/card/CardMedia';
import FormControlLabel from 'styles/form/FormControlLabel';
import Radio from 'styles/radio/Radio';
import RadioGroup from 'styles/radio/RadioGroup';
import { SelectProps } from 'styles/select/Select';
import Stack from 'styles/stack/Stack';
import { theme } from 'styles/theme/ThemeStyle';
import { ECarriersList, IClient } from 'types/Carrier';
import { SECTIONS } from 'types/SectionsVisibility';
import { useTranslation } from 'utils/translations';
import DateOfService from './components/DateOfService.component';
import FormFiscalCode from './forms/FormFiscalCode';
import FormMemberData from './forms/FormMemberData';
import FormMemberID from './forms/FormMemberID';
import FormPpsCode from './forms/FormPPS';
import ListMembers from './list/ListMembers';

type Props = SelectProps;
const StyledSelect = styled(Select)<Props>``;

const CheckSubmitClaim: FC = () => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const dispatch = useDispatch<AppDispatch>();
	const { members } = useSelector((store: RootState) => store.members);
	const { clients } = useSelector((store: RootState) => store.clients);
	const { dateOfService } = useSelector((store: RootState) => store.form);
	const [filter, setFilter] = useState<string | null>(null);
	const [selectedClient, setSelectedClient] = useState<IClient | undefined>();
	const { isDesktop } = useDevice();
	const { searchMemberCarrierCustomization } = useCheckEligibility();

	useEffect(() => {
		dispatch(resetMembers());
		dispatch(resetMember());
	}, [filter, dateOfService, dispatch]);

	useEffect(() => {
		return () => {
			dispatch(resetDateOfService());
		};
	}, [dispatch]);

	const handleChange = (client: IClient) => {
		setSelectedClient(client);
		searchMemberCarrierCustomization(client, setFilter);
	};

	return (
		<Fragment>
			<Box sx={{ height: '100%' }}>
				<Stack
					direction={'row'}
					height={'100%'}
					justifyContent={'space-between'}
					overflow="hidden"
				>
					<BoxOverflow
						variant="y"
						sx={{
							width: { sm: !!members?.length ? 'auto' : '100%', lg: 900 },
							mt: { sm: 0.625, lg: 7.37 },
							mb: { sm: 0.625, lg: 6.25 },
						}}
					>
						<Stack width="100%" direction="row" gap="30px">
							<Stack
								sx={{
									width: { sm: 340, lg: 368 },
									mb: 2,
								}}
								rowGap={{ sm: 3.75, lg: 6.25 }}
							>
								<FormControl>
									<InputLabel
										sx={{
											'&.Mui-focused': {
												color: theme.palette.grey2.main,
											},
										}}
									>
										{tClientLabels('check-and-submit.searchLabels.client')}
									</InputLabel>
									<StyledSelect
										value={selectedClient?.code || ''}
										renderValue={() => (
											<Typography variant="value2">
												{selectedClient?.name || ''}
											</Typography>
										)}
										variant="filled"
									>
										{clients.map(client => (
											<MenuItem
												key={client.code}
												value={client.code}
												disableTouchRipple
												onClick={() => handleChange(client)}
											>
												<Radio checked={selectedClient?.code === client.code} />
												<SelectItemText>{client.name}</SelectItemText>
											</MenuItem>
										))}
									</StyledSelect>
								</FormControl>

								<Stack direction="row" gap="30px">
									<Stack direction="column" gap="30px">
										<DateOfService selectedClient={selectedClient} />
										<SectionVisible
											carrier={selectedClient?.carrier}
											sectionName={SECTIONS.RADIO_MEMBER_SELECTION}
										>
											<RadioGroup
												name="filter"
												value={filter || ''}
												onChange={e => setFilter(e.target.value)}
												sx={{ rowGap: 2.5 }}
												role="radiogroup"
												aria-label="from group"
											>
												<SectionVisible
													carrier={selectedClient?.carrier}
													sectionName={SECTIONS.FISCAL_CODE_RADIO}
												>
													<FormControlLabel
														sx={{ width: '100%' }}
														value="inputByFiscalCode"
														label={tClientLabels(
															'check-and-submit.searchLabels.searchByFiscalCode',
														)}
														control={<Radio />}
														componentsProps={{ typography: { variant: 'value2' } }}
													/>
												</SectionVisible>
												<SectionVisible
													carrier={selectedClient?.carrier}
													sectionName={SECTIONS.PPS_CODE}
												>
													<FormControlLabel
														sx={{ width: '100%' }}
														value="inputByPPS"
														label={tClientLabels('check-and-submit.searchLabels.searchByPps')}
														control={<Radio />}
														componentsProps={{ typography: { variant: 'value2' } }}
													/>
												</SectionVisible>
												<FormControlLabel
													value="inputByMemberID"
													label={tClientLabels(
														'check-and-submit.searchLabels.searchByMemberID',
													)}
													control={<Radio />}
													componentsProps={{ typography: { variant: 'value2' } }}
												/>
												<FormControlLabel
													value="inputByMemberName"
													label={tClientLabels(
														'check-and-submit.searchLabels.searchByMemberName',
													)}
													control={<Radio />}
													componentsProps={{ typography: { variant: 'value2' } }}
												/>
											</RadioGroup>
										</SectionVisible>
									</Stack>
									<Box
										minWidth={{
											sm: '340px',
											lg: '368px',
										}}
									>
										{selectedClient && (
											<>
												{filter === 'inputByFiscalCode' && (
													<FormFiscalCode selectedClient={selectedClient} />
												)}
												{filter === 'inputByMemberID' && (
													<FormMemberID selectedClient={selectedClient} />
												)}
												{filter === 'inputByMemberName' && (
													<FormMemberData selectedClient={selectedClient} />
												)}
												{filter === 'inputByPPS' && (
													<FormPpsCode selectedClient={selectedClient} />
												)}
											</>
										)}
									</Box>
								</Stack>
							</Stack>
						</Stack>
						<Stack mt={{ sm: '30px', lg: '50px' }}>
							{selectedClient && (
								<ListMembers
									selectedCarrier={selectedClient.carrier.customLayout as ECarriersList}
								/>
							)}
						</Stack>
					</BoxOverflow>
					{isDesktop && (
						<Stack
							height={'100%'}
							alignSelf={'end'}
							justifyContent={'end'}
							alignItems={'end'}
							display={{ sm: !!members?.length ? 'none' : 'flex', lg: 'flex' }}
						>
							<CardMedia
								component="img"
								alt={tClientLabels('check-and-submit.image.alt')}
								sx={{
									width: { sm: 371.95, lg: '480px' },
									height: { sm: 350.93, lg: 'auto' },
									mb: { sm: 5, lg: 6.25 },
								}}
								image={CheckSubmitImage}
							/>
						</Stack>
					)}
				</Stack>
			</Box>
		</Fragment>
	);
};
export default CheckSubmitClaim;
